import * as turf from '@turf/turf';
import maplibregl from 'maplibre-gl';

export const fitBounds = (map: maplibregl.Map, geojson: GeoJSON.FeatureCollection) => {
  const bounds = turf.bbox(geojson).slice(0, 4) as [number, number, number, number];
  map.fitBounds(bounds, {
    padding: 40,
    duration: 1000,
  });
};

export const initialiseCatchmentSource = (map: maplibregl.Map, id: string) => {
  map.addSource(id, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  });
};

export const clearCatchmentSource = (source: maplibregl.GeoJSONSource) => {
  source.setData({
    type: 'FeatureCollection',
    features: [],
  });
};

export const initialiseCatchmentLayers = (
  map: maplibregl.Map,
  fillId: string,
  outlineId: string
) => {
  map.addLayer({
    id: fillId,
    type: 'fill',
    source: 'catchment',
    layout: {},
    paint: {
      'fill-color': '#5C7CFA',
      'fill-opacity': 0.1,
    },
  });
  map.addLayer({
    id: outlineId,
    type: 'line',
    source: 'catchment',
    layout: {},
    paint: {
      'line-color': '#4C6EF5',
      'line-width': 2,
    },
  });
};

export const initialiseRainfallEventBoundarySource = (map: maplibregl.Map, id: string) => {
  map.addSource(id, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  });
};

export const clearRainfallEventBoundarySource = (source: maplibregl.GeoJSONSource) => {
  source.setData({
    type: 'FeatureCollection',
    features: [],
  });
};

export const initialiseRainfallEventBoundaryLayer = (map: maplibregl.Map, outlineId: string) => {
  map.addLayer({
    id: outlineId,
    type: 'line',
    source: 'rainfall-event-area',
    layout: {},
    paint: {
      'line-color': '#4C6EF5',
      'line-width': 2,
    },
  });
};

export const initialiseRainfallImageLayers = (map: maplibregl.Map) => {
  // Transparent placeholder image
  const transparentImage =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==';

  // two raster sources & layers for crossfading
  for (let i = 1; i <= 2; i++) {
    const sourceId = `rainfall_source_${i}`;
    const layerId = `rainfall_layer_${i}`;

    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: 'image',
        url: transparentImage,
        coordinates: [
          [-0.185754, 51.684825],
          [-0.00908, 51.684825],
          [-0.00908, 51.60585],
          [-0.185754, 51.60585],
        ],
      });

      map.addLayer({
        id: layerId,
        source: sourceId,
        type: 'raster',
        paint: {
          'raster-opacity': 0,
          'raster-fade-duration': 1000,
        },
        layout: {
          visibility: 'none',
        },
      });
    }
  }
};

// to track which layer is currently active
let activeLayerIndex = 1;

// update image based on the date
export const updateRainfallImage = async (map: maplibregl.Map, dateStep: string) => {
  activeLayerIndex = activeLayerIndex === 1 ? 2 : 1;
  const nextLayerIndex = activeLayerIndex === 1 ? 2 : 1;

  const activeLayerId = `rainfall_layer_${activeLayerIndex}`;
  const nextSourceId = `rainfall_source_${nextLayerIndex}`;
  const nextLayerId = `rainfall_layer_${nextLayerIndex}`;

  const day = dateStep.split('/')[0];
  const imageUrl = `https://suds.slalomdev.io/mapdata/preprocessed/acc_rainfall_${day}feb_test.png`;

  const image = new Image();
  image.crossOrigin = 'anonymous';

  // to ensure image loads before animations start
  return new Promise((resolve, reject) => {
    image.onload = () => {
      try {
        // Update the next layer's source first while it's still invisible
        if (map.getSource(nextSourceId)) {
          (map.getSource(nextSourceId) as maplibregl.ImageSource).updateImage({
            url: imageUrl,
            coordinates: [
              [-0.185754, 51.684825],
              [-0.00908, 51.684825],
              [-0.00908, 51.60585],
              [-0.185754, 51.60585],
            ],
          });

          map.setLayoutProperty(nextLayerId, 'visibility', 'visible');
          map.setPaintProperty(nextLayerId, 'raster-opacity', 0);

          // delay to ensure the layer is properly initialised before transition
          setTimeout(() => {
            if (map.getLayer(activeLayerId)) {
              map.setLayoutProperty(activeLayerId, 'visibility', 'visible');
            }

            map.setPaintProperty(activeLayerId, 'raster-opacity', 0);
            map.setPaintProperty(nextLayerId, 'raster-opacity', 0.65);

            setTimeout(() => {
              map.setLayoutProperty(activeLayerId, 'visibility', 'none');
              resolve(true);
            }, 1000);
          }, 50);
        } else {
          reject(new Error(`Source ${nextSourceId} not found`));
        }
      } catch (error) {
        reject(error);
      }
    };

    image.onerror = () => {
      console.error('Failed to load rainfall image for:', dateStep);
      reject(new Error(`Failed to load image for ${dateStep}`));
    };

    image.src = imageUrl;
  });
};

export const initialiseRunoffImageLayers = (map: maplibregl.Map) => {
  // Transparent placeholder image
  const transparentImage =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg==';

  // two raster sources & layers for crossfading
  for (let i = 1; i <= 2; i++) {
    const sourceId = `runoff_source_${i}`;
    const layerId = `runoff_layer_${i}`;

    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, {
        type: 'image',
        url: transparentImage,
        coordinates: [
          [-0.185754, 51.684825],
          [-0.00908, 51.684825],
          [-0.00908, 51.60585],
          [-0.185754, 51.60585],
        ],
      });

      map.addLayer({
        id: layerId,
        source: sourceId,
        type: 'raster',
        paint: {
          'raster-opacity': 0,
          'raster-fade-duration': 1000,
        },
        layout: {
          visibility: 'none',
        },
      });
    }
  }
};

// to track which layer is currently active
let activeRunoffLayerIndex = 1;

// update image based on the date
export const updateRunoffImage = async (
  map: maplibregl.Map,
  dateStep: string,
  isOptimalMode: boolean
) => {
  activeRunoffLayerIndex = activeRunoffLayerIndex === 1 ? 2 : 1;
  const nextLayerIndex = activeRunoffLayerIndex === 1 ? 2 : 1;

  const activeLayerId = `runoff_layer_${activeRunoffLayerIndex}`;
  const nextSourceId = `runoff_source_${nextLayerIndex}`;
  const nextLayerId = `runoff_layer_${nextLayerIndex}`;

  const day = dateStep.split('/')[0];
  let imageUrl;
  if (isOptimalMode) {
    imageUrl = `https://suds.slalomdev.io/mapdata/preprocessed/post_runoff_${day}Feb_test.png`;
  } else {
    imageUrl = `https://suds.slalomdev.io/mapdata/preprocessed/Runoff_${day}Feb_test.png`;
  }

  const image = new Image();
  image.crossOrigin = 'anonymous';

  // to ensure image loads before animations start
  return new Promise((resolve, reject) => {
    image.onload = () => {
      try {
        // Update the next layer's source first while it's still invisible
        if (map.getSource(nextSourceId)) {
          (map.getSource(nextSourceId) as maplibregl.ImageSource).updateImage({
            url: imageUrl,
            coordinates: [
              [-0.185754, 51.684825],
              [-0.00908, 51.684825],
              [-0.00908, 51.60585],
              [-0.185754, 51.60585],
            ],
          });

          map.setLayoutProperty(nextLayerId, 'visibility', 'visible');
          map.setPaintProperty(nextLayerId, 'raster-opacity', 0);

          // delay to ensure the layer is properly initialised before transition
          setTimeout(() => {
            if (map.getLayer(activeLayerId)) {
              map.setLayoutProperty(activeLayerId, 'visibility', 'visible');
            }

            map.setPaintProperty(activeLayerId, 'raster-opacity', 0);
            map.setPaintProperty(nextLayerId, 'raster-opacity', 0.5);

            setTimeout(() => {
              map.setLayoutProperty(activeLayerId, 'visibility', 'none');
              resolve(true);
            }, 1000);
          }, 50);
        } else {
          reject(new Error(`Source ${nextSourceId} not found`));
        }
      } catch (error) {
        reject(error);
      }
    };

    image.onerror = () => {
      console.error('Failed to load runoff image for:', dateStep);
      reject(new Error(`Failed to load image for ${dateStep}`));
    };

    image.src = imageUrl;
  });
};
